import {useState} from 'react';
import {CustomerSystem} from '@/store/customerFile.ts';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody} from '@/components/ui/dialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Label} from '@/components/ui/label.tsx';
import {SignalIcon, CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {Spinner} from '@/components/ui/spinner.tsx';

interface Props {
    open: boolean;
    onClose: () => void;
    system: CustomerSystem;
}

const DURATION_OPTIONS = [
    {key: '3days', label: '3 ימים', price: '₪\u00a01.00 ח"פ לערוץ'},
    {key: '1week', label: 'שבוע', price: '₪\u00a01.50 ח"פ לערוץ'},
    {key: 'permanent', label: 'קבוע', price: '₪\u00a02.50 חודשי לערוץ'},
] as const;
type DurationType = typeof DURATION_OPTIONS[number]['key'];

const PRICE_MAP: Record<DurationType, number> = {
    '3days': 1.0,
    '1week': 1.5,
    'permanent': 2.5,
};

export function BuyChannelsModal({open, onClose, system}: Props) {
    const [channelCount, setChannelCount] = useState(1);
    const [duration, setDuration] = useState<DurationType>('permanent');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const total = channelCount * PRICE_MAP[duration];

    function handleClose() {
        setChannelCount(1);
        setDuration('permanent');
        setSubmitting(false);
        setDone(false);
        setError('');
        onClose();
    }

    async function handleSubmit() {
        if (channelCount < 1) return;
        setSubmitting(true);
        setError('');
        try {
            const resp = await fetch('/api/customer-file/buy-channels/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({school_id: system.id, channel_count: channelCount, duration_type: duration}),
            });
            const data = await resp.json();
            if (!resp.ok || !data.status) {
                setError(data.message || 'שגיאה בביצוע הרכישה');
                return;
            }
            setDone(true);
        } catch {
            setError('שגיאת רשת, נסה שוב');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent dir="rtl" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <SignalIcon className="w-5 h-5 text-blue-600" />
                        רכישת ערוצים — {system.name}
                    </DialogTitle>
                </DialogHeader>

                {done ? (
                    <DialogBody className="flex flex-col items-center gap-3 py-6">
                        <CheckCircleIcon className="w-12 h-12 text-green-500" />
                        <p className="text-sm font-medium text-gray-800">הרכישה בוצעה בהצלחה</p>
                        <p className="text-xs text-gray-500">נוספו {channelCount} ערוצים למערכת {system.name}.</p>
                        <p className="text-xs text-gray-400">חשבונית נשלחה לסליקה.</p>
                        <Button onClick={handleClose} className="mt-2">סגור</Button>
                    </DialogBody>
                ) : (
                    <DialogBody className="space-y-5 py-2">
                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
                            מערכת: <strong>{system.name}</strong> | ערוצים נוכחיים: <strong>{system.amount_of_lines === -1 ? '∞' : system.amount_of_lines}</strong>
                        </div>

                        <div className="space-y-1.5 w-1/2">
                            <Label>כמות ערוצים לרכישה</Label>
                            <Input
                                type="number"
                                min={1}
                                value={channelCount}
                                onChange={(e) => setChannelCount(Math.max(1, parseInt(e.target.value) || 1))}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>תקופה</Label>
                            {DURATION_OPTIONS.map((opt) => (
                                <label
                                    key={opt.key}
                                    className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${duration === opt.key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="duration"
                                            value={opt.key}
                                            checked={duration === opt.key}
                                            onChange={() => setDuration(opt.key)}
                                            className="accent-blue-600"
                                        />
                                        <span className="text-sm font-medium text-gray-800">{opt.label}</span>
                                    </div>
                                    <span className="text-sm text-gray-500">{opt.price}</span>
                                </label>
                            ))}
                        </div>

                        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm flex justify-between items-center">
                            <span className="text-gray-600">סה"כ לחיוב</span>
                            <span className="text-lg font-semibold text-gray-900">
                                {/* X ערוצים (עד לתאריך DD/MM/YYYY | תוספת חודשית קבועה): CC ₪ */}
                                {channelCount} ערוצים × {PRICE_MAP[duration].toLocaleString('he-IL', {style: 'currency', currency: 'ILS'})} = <span className="text-blue-600">{total.toLocaleString('he-IL', {style: 'currency', currency: 'ILS'})}</span>
                                

                                
                            </span>
                        </div>

                        {error && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5" />{error}
                            </p>
                        )}
                    </DialogBody>
                )}

                {!done && (
                    <DialogFooter className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={handleClose}>ביטול</Button>
                        <Button onClick={handleSubmit} disabled={submitting || channelCount < 1}>
                            {submitting ? <Spinner size="sm" /> : `רכוש — ₪${total.toLocaleString('he-IL', {minimumFractionDigits: 2})}`}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}

function getCsrfToken(): string {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : '';
}
